import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Card, CardContent, Typography, Box, Button } from "@mui/material";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import { TraduccionesContext } from "components/shared/TraduccionesContext";

const BRAND_PRIMARY = "#4B5DA2";

const normalizeServiceKey = (value = "") =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const getServiceIcon = (title, seoURLFriendly) => {
  const key = normalizeServiceKey(`${seoURLFriendly} ${title}`);

  if (key.includes("amistoso")) return ThumbUpOutlinedIcon;
  if (key.includes("judicial")) return HomeOutlinedIcon;
  if (key.includes("defensa")) return ShieldOutlinedIcon;
  if (key.includes("cartera") || key.includes("npl")) return CreditCardOutlinedIcon;
  if (key.includes("inversor")) return TrendingUpOutlinedIcon;
  if (key.includes("back office") || key.includes("backoffice")) return GridViewOutlinedIcon;

  return BusinessCenterOutlinedIcon;
};

const stripHtml = (html = "") => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/** Card para la sección "Más soluciones" de la home (diseño claro, icono circular). */
export const ServiceCardSolutions = ({
  title,
  content,
  url,
  seoURLFriendly,
  hideReadMore = false,
}) => {
  const { idioma } = useContext(TraduccionesContext);
  const IconComponent = getServiceIcon(title, seoURLFriendly);
  const plainDescription = stripHtml(content);
  const buttonLabel = String(idioma) === "2" ? "Read more" : "Leer más";

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: "280px",
        height: "100%",
        minHeight: "300px",
        borderRadius: "14px",
        boxShadow: "0 4px 20px rgba(75, 93, 162, 0.12)",
        backgroundColor: "#f5f6fa",
        display: "flex",
        flexDirection: "column",
        "& *": { fontFamily: "Nunito !important" },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: {
            xs: "32px 20px",
            sm: "36px 22px",
          },
          "&:last-child": { paddingBottom: "36px" },
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: BRAND_PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
            flexShrink: 0,
          }}
        >
          <IconComponent sx={{ color: "#FFF", fontSize: 26 }} />
        </Box>

        <Typography
          component="h3"
          sx={{
            fontSize: { xs: "18px", sm: "20px" },
            lineHeight: 1.25,
            fontWeight: "bold",
            color: "#212121",
            marginBottom: "12px",
          }}
        >
          {title}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: "14px",
            lineHeight: "21px",
            color: "#757575",
            flexGrow: 1,
            marginBottom: "20px",
            maxWidth: "240px",
          }}
        >
          {plainDescription}
        </Typography>

        {!hideReadMore && seoURLFriendly?.trim() !== "" && (
          <Link to={"/" + url} style={{ textDecoration: "none", marginTop: "auto" }}>
            <Button
              variant="outlined"
              sx={{
                borderRadius: "50px",
                borderWidth: "1px",
                borderColor: BRAND_PRIMARY,
                color: BRAND_PRIMARY,
                fontSize: "14px",
                fontWeight: 500,
                letterSpacing: "0.01em",
                textTransform: "none",
                padding: "9px 42px",
                minWidth: "unset",
                lineHeight: 1.2,
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:hover": {
                  borderWidth: "1px",
                  borderColor: BRAND_PRIMARY,
                  color: BRAND_PRIMARY,
                  backgroundColor: "rgba(75, 93, 162, 0.06)",
                  boxShadow: "none",
                },
              }}
            >
              {buttonLabel}
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

ServiceCardSolutions.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  seoURLFriendly: PropTypes.string,
  hideReadMore: PropTypes.bool,
};
